<template>
    <div class="relative" ref="containerRef">
        <label
            v-if="label"
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
            {{ label }} <span v-if="required" class="text-red-500">*</span>
        </label>

        <!-- Selected Value Display / Search Input -->
        <div
            @click="toggleDropdown"
            class="w-full px-3 py-2 border rounded-md cursor-pointer flex items-center justify-between dark:bg-gray-700 dark:text-white transition-colors"
            :class="{
                'border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500':
                    !error,
                'border-red-500': error,
                'bg-gray-100 dark:bg-gray-600': disabled,
            }"
        >
            <span v-if="selectedOption" class="truncate">
                {{ displayValue }}
            </span>
            <span v-else class="text-gray-500 dark:text-gray-400 truncate">
                {{ placeholder }}
            </span>
            <svg
                class="h-4 w-4 ml-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                />
            </svg>
        </div>

        <!-- Error Message -->
        <p v-if="error && error.length > 0" class="mt-1 text-sm text-red-600">
            {{ error }}
        </p>

        <!-- Dropdown -->
        <div
            v-if="isOpen"
            class="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
            <!-- Search Input -->
            <div class="p-2 border-b border-gray-200 dark:border-gray-700">
                <input
                    ref="searchInputRef"
                    v-model="searchQuery"
                    type="text"
                    placeholder="Search..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm"
                />
            </div>

            <!-- Options List -->
            <div v-if="filteredOptions.length > 0">
                <div
                    v-for="option in filteredOptions"
                    :key="getOptionKey(option)"
                    @click="selectOption(option)"
                    class="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    :class="{
                        'bg-blue-50 dark:bg-blue-900/30': isSelected(option),
                    }"
                >
                    {{ getOptionLabel(option) }}
                </div>
            </div>
            <div
                v-else
                class="px-3 py-4 text-center text-gray-500 dark:text-gray-400 text-sm"
            >
                No results found
            </div>
        </div>

        <!-- Click outside to close -->
        <div
            v-if="isOpen"
            @click="closeDropdown"
            class="fixed inset-0 z-40"
        ></div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";

interface Props {
    modelValue: any;
    options: any[];
    label?: string;
    placeholder?: string;
    optionLabel?: string | ((option: any) => string);
    optionValue?: string | ((option: any) => any);
    required?: boolean;
    disabled?: boolean;
    error?: string | string[];
}

interface Emits {
    (e: "update:modelValue", value: any): void;
}

const props = withDefaults(defineProps<Props>(), {
    label: "",
    placeholder: "Select an option",
    required: false,
    disabled: false,
    error: "",
});

const emit = defineEmits<Emits>();

const isOpen = ref(false);
const searchQuery = ref("");
const containerRef = ref<HTMLElement | null>(null);
const searchInputRef = ref<HTMLInputElement | null>(null);

// Get the label for an option
const getOptionLabel = (option: any): string => {
    if (typeof props.optionLabel === "function") {
        return props.optionLabel(option);
    }
    if (props.optionLabel) {
        return option[props.optionLabel];
    }
    // Default: try to find a reasonable label
    return (
        option.name ||
        option.title ||
        option.display_name ||
        option.user?.name ||
        option.label ||
        String(option)
    );
};

// Get the value for an option
const getOptionValue = (option: any): any => {
    if (typeof props.optionValue === "function") {
        return props.optionValue(option);
    }
    if (props.optionValue) {
        return option[props.optionValue];
    }
    // Default: use id or the option itself
    return option.id || option;
};

// Get a unique key for an option
const getOptionKey = (option: any): string => {
    return String(getOptionValue(option));
};

// Check if an option is selected
const isSelected = (option: any): boolean => {
    return getOptionValue(option) === props.modelValue;
};

// Get the currently selected option
const selectedOption = computed(() => {
    if (props.modelValue === undefined || props.modelValue === null)
        return null;
    return props.options.find(
        (opt) => getOptionValue(opt) === props.modelValue,
    );
});

// Get the display value for the selected option
const displayValue = computed(() => {
    if (!selectedOption.value) return "";
    return getOptionLabel(selectedOption.value);
});

// Filter options based on search query
const filteredOptions = computed(() => {
    if (!searchQuery.value.trim()) {
        return props.options;
    }

    const query = searchQuery.value.toLowerCase();
    return props.options.filter((option) => {
        const label = getOptionLabel(option).toLowerCase();
        return label.includes(query);
    });
});

// Toggle dropdown
const toggleDropdown = () => {
    if (props.disabled) return;
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
        nextTick(() => {
            searchInputRef.value?.focus();
        });
    }
};

// Close dropdown
const closeDropdown = () => {
    isOpen.value = false;
    searchQuery.value = "";
};

// Select an option
const selectOption = (option: any) => {
    emit("update:modelValue", getOptionValue(option));
    closeDropdown();
};

// Handle click outside
const handleClickOutside = (event: MouseEvent) => {
    if (
        containerRef.value &&
        !containerRef.value.contains(event.target as Node)
    ) {
        closeDropdown();
    }
};

// Handle escape key
const handleEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
        closeDropdown();
    }
};

// Watch for model value changes to reset search
watch(
    () => props.modelValue,
    () => {
        searchQuery.value = "";
    },
);

// Lifecycle hooks
onMounted(() => {
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
});

onUnmounted(() => {
    document.removeEventListener("click", handleClickOutside);
    document.removeEventListener("keydown", handleEscape);
});
</script>

<style scoped>
/* Custom scrollbar for dropdown */
.overflow-y-auto::-webkit-scrollbar {
    width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.dark .overflow-y-auto::-webkit-scrollbar-track {
    background: #374151;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #555;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #6b7280;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
}
</style>
